import React, { useMemo } from 'react';
import type { WebhookProviderConfigViewProps } from '../types';
import { WebhookProviderConnectionStatus } from '../shared/webhook-provider-connection-status';
import { WebhookProviderCredentialSection } from '../shared/webhook-provider-credential-section';
import { WebhookSetupKindSection } from '../shared/webhook-setup-kind-section';
import { WebhookEventPicker } from '../shared/webhook-event-picker';
import { CatalogSchemaForm } from '../shared/catalog-schema-form';
import { WebhookVerificationSection } from '../shared/webhook-verification-section';
import { WebhookEndpointPanel } from '../shared/webhook-endpoint-panel';
import { WebhookInstallStatusSection } from '../shared/webhook-install-status-section';
import { WebhookProviderSecretSection } from '../shared/webhook-provider-secret-section';

export const DefaultWebhookProviderConfigView: React.FC<
  WebhookProviderConfigViewProps
> = ({
  mode,
  catalog,
  draft,
  onDraftChange,
  triggerId,
  deliveryUrl,
  installStatus,
  isDraft,
  secret,
  onCredentialStatusChange,
}) => {
  const selectedEvent = useMemo(
    () => catalog.events.find((event) => event.key === draft.eventKey),
    [catalog.events, draft.eventKey]
  );
  const showEventPicker =
    catalog.setup.kind !== 'none' || catalog.events.length > 0;
  const needsProviderSecretInput =
    catalog.setup.kind === 'manual_instructions' &&
    (catalog.endpoint.secretKind === 'provider_supplied' ||
      catalog.endpoint.secretKind === 'both');

  return (
    <div className="space-y-4">
      {/*
       * No provider name or description here. Every caller already names the
       * provider in its own heading — the wizard's configure step, the create
       * drawer's title, the edit drawer's trigger title — so repeating it made
       * "Linear" appear three times on one screen. The catalog description now
       * rides in the wizard's subtitle, where it is the only thing on screen
       * saying what the provider covers.
       */}
      <WebhookProviderConnectionStatus catalog={catalog} />

      <WebhookProviderCredentialSection
        catalog={catalog}
        onConnectedChange={onCredentialStatusChange}
      />

      <WebhookSetupKindSection catalog={catalog} />

      {showEventPicker ? (
        <WebhookEventPicker
          catalog={catalog}
          selectedEventKey={draft.eventKey}
          onSelect={(eventKey) => onDraftChange({ eventKey, eventConfig: {} })}
        />
      ) : null}

      {selectedEvent?.requiresConfig === true &&
      selectedEvent.configSchema !== undefined ? (
        <CatalogSchemaForm
          schema={selectedEvent.configSchema}
          value={draft.eventConfig}
          onChange={(eventConfig) => onDraftChange({ eventConfig })}
        />
      ) : null}

      {needsProviderSecretInput ? (
        <WebhookProviderSecretSection
          catalog={catalog}
          value={draft.endpointSecret}
          onChange={(endpointSecret) => onDraftChange({ endpointSecret })}
        />
      ) : null}

      {catalog.key === 'generic' ? (
        <WebhookVerificationSection
          verificationConfig={draft.verificationConfig}
          verificationConfigSchema={catalog.verificationConfigSchema}
          onChange={(verificationConfig) =>
            onDraftChange({ verificationConfig })
          }
        />
      ) : null}

      {mode === 'edit' ? (
        <WebhookInstallStatusSection
          catalog={catalog}
          triggerId={triggerId}
          isDraft={isDraft}
          installStatus={installStatus}
        />
      ) : null}

      {triggerId !== undefined ||
      deliveryUrl !== undefined ||
      secret !== undefined ? (
        <WebhookEndpointPanel
          deliveryUrl={deliveryUrl}
          secret={needsProviderSecretInput ? undefined : secret}
          secretLabel={
            catalog.key === 'generic'
              ? 'Webhook secret / auth token'
              : undefined
          }
          secretDescription={
            catalog.key === 'generic'
              ? draft.verificationConfig !== null &&
                typeof draft.verificationConfig === 'object' &&
                'profile' in draft.verificationConfig &&
                draft.verificationConfig.profile === 'custom'
                ? 'Use this value as the HMAC signing key for the configured signature format.'
                : 'Send this value as Authorization, x-cotera-webhook-token, cotera_webhook_token, or auth_token.'
              : undefined
          }
          singleWebhookEndpoint={catalog.endpoint.singleWebhookEndpoint}
        />
      ) : null}
    </div>
  );
};
